import { useCallback, useSyncExternalStore } from "react";
import { tasksSubscription } from "@/lib/firestore/subscriptions/tasks";
import { NO_WEDDING_KEY } from "@/lib/firestore/subscription-keys";
import {
  EMPTY_TASKS,
  TASKS_SNAPSHOT_EMPTY,
  TASKS_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import { useWedding } from "./useWedding";
import type { Task } from "@amazing-wedding/shared-types";

interface UseTasksResult {
  tasks: Task[];
  loading: boolean;
}

/**
 * Subscribes to /weddings/{wid}/tasks. Stable order by createdAt ASC;
 * consumers re-sort (the Kanban groups by status, the list view by
 * dueDate / priority / etc).
 *
 * Wedding task lists are tiny (~10–100 docs) — we pull everything into
 * memory and filter client-side, same pattern as useGuests.
 *
 * Backed by a refcounted shared Firestore subscription keyed by weddingId.
 */
export function useTasks(): UseTasksResult {
  const { wedding, loading: weddingLoading } = useWedding();
  const key = wedding?.id ?? NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!wedding?.id || weddingLoading) return () => {};
      return tasksSubscription.subscribe(key, listener);
    },
    [wedding?.id, weddingLoading, key],
  );

  const getSnapshot = useCallback(() => {
    if (weddingLoading) return TASKS_SNAPSHOT_LOADING;
    if (!wedding?.id) return TASKS_SNAPSHOT_EMPTY;
    return tasksSubscription.getSnapshot(key);
  }, [wedding?.id, weddingLoading, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => TASKS_SNAPSHOT_LOADING);

  if (weddingLoading) {
    return { tasks: EMPTY_TASKS, loading: true };
  }
  if (!wedding) {
    return { tasks: EMPTY_TASKS, loading: false };
  }

  return { tasks: snapshot.data, loading: snapshot.loading };
}
